import { DateOnly } from "@/lib/date-only"
import { ChConcludeBillingSchemaType } from "@/modules/common/billing/components/billing-form/ch/conclude/schema/ch-conclude-billing-schema"
import { EMPLOYER_INSURANCE } from "@/modules/common/billing/components/billing-form/constants"
import { DeBillingSchemaType } from "@/modules/common/billing/components/billing-form/de/schema/de-billing-schema"
import { CollectBillingDataInput } from "@/modules/patients/detail/conclude/billing-section/services/__generated__/useCollectBillingDataMutation.graphql"
import { Country } from "@/types/__generated__/union-types"

export type BillingFormData = DeBillingSchemaType & ChConcludeBillingSchemaType

type Context = {
  appointmentId: string
  country: Country
  patientAge: number | undefined
  patientOrganisationKey: string | null
  isPatientPregnant: boolean
}

const ACCIDENT_ELIGIBLE_MIN_AGE = 18
const ACCIDENT_ELIGIBLE_MAX_AGE = 65

export const mapBillingFormToCollectInput = (
  billingData: BillingFormData,
  context: Context,
): CollectBillingDataInput => {
  const { appointmentId, country, patientAge, patientOrganisationKey, isPatientPregnant } = context
  const {
    isNotBillable,
    reason,
    notBillableReason,
    treatmentReason,
    currentGestationWeek,
    dateOfAccident,
    organisationId,
    caseReference,
    accidentReference,
  } = billingData

  const base: CollectBillingDataInput = { appointmentId, isNotBillable, isPatientPregnant }

  if (isNotBillable) {
    return {
      ...base,
      reason: notBillableReason === "OTHER_REASON" ? reason : null,
      notBillableReason,
    }
  }

  if (country === "CH") {
    const isEmployerInsurance = organisationId === EMPLOYER_INSURANCE
    const isAccidentEligibleAge =
      patientAge === undefined || (patientAge >= ACCIDENT_ELIGIBLE_MIN_AGE && patientAge <= ACCIDENT_ELIGIBLE_MAX_AGE)

    const pregnancyFields =
      isPatientPregnant && currentGestationWeek ? { currentGestationWeek: Number(currentGestationWeek) } : {}

    const accidentEligibleAgeFields = isAccidentEligibleAge
      ? {
          organisationId: organisationId && organisationId.length > 0 && !isEmployerInsurance ? organisationId : null,
          isEmployer: isEmployerInsurance || null,
          accidentReference: accidentReference || null,
        }
      : {}

    const accidentFields =
      treatmentReason === "ACCIDENT"
        ? {
            dateOfAccident: dateOfAccident ? new DateOnly(dateOfAccident).toString() : null,
            ...accidentEligibleAgeFields,
          }
        : {}

    const congenitalDefectFields =
      treatmentReason === "CONGENITAL_DEFECT" ? { ivgCaseReference: caseReference || null } : {}

    return {
      ...base,
      treatmentReason,
      patientOrganisationKey,
      patientAge: patientAge ?? null,
      isPatientPregnant,
      ...pregnancyFields,
      ...accidentFields,
      ...congenitalDefectFields,
    }
  }

  if (country === "DE") {
    return {
      ...base,
      isVisualSymptomExamination: billingData.isVisualSymptomExamination,
    }
  }

  return base
}
